import { Modal } from 'ant-design-vue';
import { featureDescriptions } from 'src/config/features-config.js';
import { mapActions, mapGetters } from 'vuex';

// Readable message from an API error body. Checks `detail`, `error` and
// `response` first, then the first field; each may be a string or an array
// of strings.
export function apiErrorMessage(error, fallback) {
  const asMessage = (value) => {
    const text = Array.isArray(value) ? value[0] : value;
    return typeof text === 'string' && text.trim() ? text : null;
  };
  const data = error?.response?.data;
  if (typeof data === 'string') {
    return data.trim() && data.length < 300 ? data : fallback;
  }
  if (!data || typeof data !== 'object') return fallback;
  if (Array.isArray(data)) return asMessage(data) || fallback;
  const candidates = [
    data.detail,
    data.error,
    data.response,
    ...Object.values(data),
  ];
  for (const candidate of candidates) {
    const message = asMessage(candidate);
    if (message) return message;
  }
  return fallback;
}

export const organizationLabel = (org) =>
  org?.org_name_to_display || org?.Org_name || '';

// Shared by the "By organization" and "By feature" tabs.
export default {
  inject: ['toast'],
  data() {
    return {
      // Keys of actions in flight, e.g. "sub:<featureId>:<Org_name>".
      busy: {},
    };
  },
  computed: {
    ...mapGetters([
      'adminFeatures',
      'adminFeatureSubscriptions',
      'isLoadingAdminFeatureControl',
      'organizations',
    ]),
    sortedFeatures() {
      return [...this.adminFeatures].sort((a, b) =>
        String(a.name).localeCompare(String(b.name))
      );
    },
    organizationOptions() {
      return [...(this.organizations || [])]
        .map((org) => ({
          value: org.Org_name,
          label: organizationLabel(org),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  },
  methods: {
    ...mapActions([
      'adminDisableFeature',
      'adminRestoreFeature',
      'adminEnableFeatureForOrganization',
      'adminRemoveFeatureSubscription',
    ]),
    organizationLabel,
    featureDescription(feature) {
      return featureDescriptions[feature.name] || 'No description';
    },
    // Match what the admin sees (display name) as well as the org key.
    filterOrganizationOption(input, option) {
      const search = input.toLowerCase();
      return [option.label, option.value].some((text) =>
        String(text ?? '')
          .toLowerCase()
          .includes(search)
      );
    },
    subscriptionsOf(feature) {
      return this.adminFeatureSubscriptions.filter(
        (item) => item.Feature?.id === feature.id
      );
    },
    organizationCount(feature) {
      return Number.isInteger(feature.subscribed_org_count)
        ? feature.subscribed_org_count
        : this.subscriptionsOf(feature).length;
    },
    organizationCountText(feature) {
      const count = this.organizationCount(feature);
      return `${count} organization${count === 1 ? '' : 's'}`;
    },
    subscriptionKey(featureId, organization) {
      return `sub:${featureId}:${organization}`;
    },
    async runAction(key, action, successMessage, failureMessage) {
      this.busy = { ...this.busy, [key]: true };
      const [error] = await action();
      const rest = { ...this.busy };
      delete rest[key];
      this.busy = rest;
      if (error) {
        this.toast.error(apiErrorMessage(error, failureMessage));
        return false;
      }
      this.toast.success(successMessage);
      return true;
    },
    enableForOrganization(feature, organization, organizationName) {
      return this.runAction(
        this.subscriptionKey(feature.id, organization),
        () =>
          this.adminEnableFeatureForOrganization({
            featureId: feature.id,
            organization,
          }),
        `${feature.name} enabled for ${organizationName}.`,
        `Could not enable ${feature.name} for ${organizationName}.`
      );
    },
    removeAccess(subscription) {
      const feature = subscription.Feature;
      const organization = subscription.Organization;
      const organizationName = organizationLabel(organization);
      return this.runAction(
        this.subscriptionKey(feature.id, organization.Org_name),
        () => this.adminRemoveFeatureSubscription(subscription.id),
        `${feature.name} removed from ${organizationName}.`,
        `Could not remove ${feature.name} from ${organizationName}.`
      );
    },
    confirmRemoveAccess(subscription) {
      const organizationName = organizationLabel(subscription.Organization);
      Modal.confirm({
        title: `Remove ${subscription.Feature.name} from ${organizationName}?`,
        content: `Only ${organizationName} loses this feature. Other organizations are not affected.`,
        okText: 'Remove access',
        cancelText: 'Cancel',
        onOk: () => this.removeAccess(subscription),
      });
    },
    confirmDisableForEveryone(feature) {
      const count = this.organizationCount(feature);
      const affected =
        count === 0
          ? 'No organization has it enabled right now.'
          : `${this.organizationCountText(
              feature
            )} will lose it immediately.`;
      Modal.confirm({
        title: `Disable ${feature.name} for everyone?`,
        content: `This turns ${feature.name} off for every organization. ${affected} Their settings are kept, so re-enabling the feature gives it back to the same organizations.`,
        okText: 'Disable for everyone',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () =>
          this.runAction(
            `feature:${feature.id}`,
            () => this.adminDisableFeature(feature.id),
            `${feature.name} is now disabled for everyone.`,
            `Could not disable ${feature.name}.`
          ),
      });
    },
    reEnable(feature) {
      return this.runAction(
        `feature:${feature.id}`,
        () => this.adminRestoreFeature(feature.id),
        `${feature.name} is active again.`,
        `Could not re-enable ${feature.name}.`
      );
    },
  },
};
